// Hydration content arithmetic, shared by client-app and coach-portal.
//
// Every field is stored per 100 ml on the drink type, so scaling to an amount
// is one multiply — alcohol, the only field that did not work that way, was
// dropped from the spec before implementation. Deriving this server-side would
// have put hydration's nutrition arithmetic in C# a few lines from meal
// nutrition arithmetic in TypeScript: client-dashboard.mappers.ts already sums
// meal macros this way, per-unit value times a quantity, the identical shape.
//
// Living in libs/shared/util is what buys the thing a backend would have: one
// implementation and one rounding rule for both frontends, the same reason
// computeOrdinalsByName sits next door in hydration-amount.ts.

/** A resolved set of content figures. Null means "no contributing value". */
export interface HydrationContent {
  sodiumMg: number | null;
  chlorideMg: number | null;
  potassiumMg: number | null;
  magnesiumMg: number | null;
  calciumMg: number | null;
  carbohydratesG: number | null;
  caffeineMg: number | null;
  caloriesKcal: number | null;
}

/**
 * A per-100 ml profile as it arrives from the API — structurally both
 * `HydrationTypeModel` and `UserHydrationEntryModel`, so callers pass either
 * without mapping. Absent fields read as null: a build that predates the
 * backend deploy sees `undefined` and hides the row, same as a null.
 */
export type HydrationContentSource = {
  [K in keyof HydrationContent]?: number | null;
};

/** A logged entry: its type's profile plus the amount it was logged at. */
export interface HydrationContentEntry extends HydrationContentSource {
  amountMilliliters?: number;
}

export type HydrationContentGroup = 'energy' | 'minerals' | 'carbs' | 'caffeine';

export interface HydrationContentField {
  key: keyof HydrationContent;
  /** Transloco key. Also the label the designs show. */
  labelKey: string;
  /** SI symbol, deliberately not translated. */
  unit: string;
  decimals: number;
  /** Which colour class the surfaces give it. */
  group: HydrationContentGroup;
}

/**
 * The eight fields in display order — energy, the five minerals, carbs,
 * caffeine — with the unit and precision each one renders at.
 *
 * Shared so the five surfaces cannot drift into different orders, different
 * decimals or a different unit for the same figure. A field keeps one unit
 * forever: never flip mg to g at a thousand, the way formatAmountLabel does for
 * the volume total. That value changes once; a content field that flips
 * mid-scroll changes glyph count and reads as a glitch.
 */
export const HYDRATION_CONTENT_FIELDS: readonly HydrationContentField[] = [
  { key: 'caloriesKcal', labelKey: 'Energy', unit: 'kcal', decimals: 0, group: 'energy' },
  { key: 'sodiumMg', labelKey: 'Sodium', unit: 'mg', decimals: 0, group: 'minerals' },
  { key: 'chlorideMg', labelKey: 'Chloride', unit: 'mg', decimals: 0, group: 'minerals' },
  { key: 'potassiumMg', labelKey: 'Potassium', unit: 'mg', decimals: 0, group: 'minerals' },
  { key: 'magnesiumMg', labelKey: 'Magnesium', unit: 'mg', decimals: 0, group: 'minerals' },
  { key: 'calciumMg', labelKey: 'Calcium', unit: 'mg', decimals: 0, group: 'minerals' },
  { key: 'carbohydratesG', labelKey: 'Carbs', unit: 'g', decimals: 1, group: 'carbs' },
  { key: 'caffeineMg', labelKey: 'Caffeine', unit: 'mg', decimals: 0, group: 'caffeine' },
];

/** The five mineral keys, which the portal surfaces nest under one parent row. */
export const HYDRATION_MINERAL_FIELDS: readonly HydrationContentField[] = HYDRATION_CONTENT_FIELDS.filter(
  (field) => field.group === 'minerals',
);

const EMPTY_CONTENT: HydrationContent = {
  sodiumMg: null,
  chlorideMg: null,
  potassiumMg: null,
  magnesiumMg: null,
  calciumMg: null,
  carbohydratesG: null,
  caffeineMg: null,
  caloriesKcal: null,
};

/** A fresh all-null set — the state every surface renders on day one. */
export function emptyHydrationContent(): HydrationContent {
  return { ...EMPTY_CONTENT };
}

/**
 * decimal arrives from the API as a float64, so 2.4 mg x 7.5 lands on
 * 17.999999. Round at the boundary rather than letting that reach a template.
 */
function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Scale a per-100 ml profile to an amount in millilitres. */
export function contentForAmount(source: HydrationContentSource | null | undefined, ml: number): HydrationContent {
  const result = emptyHydrationContent();
  if (!source) return result;

  const scale = (ml || 0) / 100;

  for (const field of HYDRATION_CONTENT_FIELDS) {
    const per100 = source[field.key];
    // Null stays null: "not sourced" is not "contains none", and only the
    // second is a claim worth rendering.
    if (per100 === null || per100 === undefined) continue;
    result[field.key] = roundTo(per100 * scale, field.decimals);
  }

  return result;
}

/**
 * Sum a day's entries. A field stays null unless at least one entry carries a
 * value for it, mirroring `anyFiber ? sum(...) : null` in the meal totals.
 *
 * That null rule is also what keeps the displayed field set stable while the
 * amount picker moves: zero times any volume is still zero, so the set of
 * fields with values changes only when the type changes.
 */
export function sumHydrationContent(entries: readonly HydrationContentEntry[] | null | undefined): HydrationContent {
  const result = emptyHydrationContent();
  if (!entries?.length) return result;

  for (const field of HYDRATION_CONTENT_FIELDS) {
    let total = 0;
    let contributed = false;

    for (const entry of entries) {
      const per100 = entry[field.key];
      if (per100 === null || per100 === undefined) continue;
      contributed = true;
      total += per100 * ((entry.amountMilliliters || 0) / 100);
    }

    // Rounded once over the summed total rather than per entry, so a day of
    // small logs does not accumulate eight roundings' worth of drift.
    if (contributed) result[field.key] = roundTo(total, field.decimals);
  }

  return result;
}

/** True when nothing has a value — the cue to hide the whole section. */
export function isHydrationContentEmpty(content: HydrationContent | null | undefined): boolean {
  if (!content) return true;
  return HYDRATION_CONTENT_FIELDS.every((field) => content[field.key] === null);
}

/** What every surface shows for a field nobody has sourced a figure for. */
export const HYDRATION_CONTENT_UNSOURCED = '—';

export interface HydrationContentDisplayField {
  field: HydrationContentField;
  value: number | null;
  /** The formatted figure, or an em dash when the field has not been sourced. */
  display: string;
  isUnsourced: boolean;
}

/**
 * The eight fields with every key present, ready to spread over an entry.
 *
 * Required keys rather than optional ones, because that is the whole point: a
 * key left out of the spread would leave the previous drink's figure behind
 * when the type changes. `undefined` rather than `null` so the result drops
 * straight into `UserHydrationEntryModel`, whose generated fields are optional
 * numbers; both read as "not sourced" everywhere that consumes them.
 */
export type HydrationContentPatch = { [K in keyof HydrationContent]: number | undefined };

export function pickHydrationContent(source: HydrationContentSource | null | undefined): HydrationContentPatch {
  const picked = {} as HydrationContentPatch;
  for (const field of HYDRATION_CONTENT_FIELDS) {
    picked[field.key] = source?.[field.key] ?? undefined;
  }
  return picked;
}

/**
 * A nutrition figure at fixed precision, in the reader's locale — "30,0" for
 * Swedish, "30.0" for English, "20 000" where that locale groups thousands.
 *
 * Fixed precision is the point: both bounds are set to `decimals` so a value
 * never gains or drops a digit as it changes, which is what would read as a
 * glitch while an amount picker moves.
 *
 * Deliberately not tied to a field, so the meal surfaces can adopt it without
 * pretending to be hydration. Falls back to en-US on an unknown locale tag, as
 * EnergyFormatPipe does.
 */
export function formatNutritionValue(value: number, decimals: number, locale?: string): string {
  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };

  try {
    return new Intl.NumberFormat(locale || 'en-US', options).format(value);
  } catch {
    return new Intl.NumberFormat('en-US', options).format(value);
  }
}

/** "231 mg", "30,0 g", "124 kcal" — locale-formatted, with the unit appended. */
export function formatHydrationContentValue(field: HydrationContentField, value: number, locale?: string): string {
  return `${formatNutritionValue(value, field.decimals, locale)} ${field.unit}`;
}

/**
 * Every field in display order, ready to render: a formatted figure where one
 * has been sourced, an em dash where none has.
 *
 * All eight, always. Every surface shows the same row set rather than one that
 * changes shape from day to day, and the single caller for the placeholder and
 * the formatting keeps the five of them from drifting apart.
 *
 * A zero is a figure, not an absence — water really does contain no caffeine —
 * so it formats as "0 mg" and only a null becomes a dash. Nulls are transient
 * anyway: a drink type is filled in when it is added, so the dash is what the
 * gap between the columns existing and the values arriving looks like.
 */
export function hydrationContentDisplayFields(
  content: HydrationContent | null | undefined,
  locale?: string,
): HydrationContentDisplayField[] {
  return HYDRATION_CONTENT_FIELDS.map((field) => {
    const value = content?.[field.key] ?? null;

    return {
      field,
      value,
      isUnsourced: value === null,
      display: value === null ? HYDRATION_CONTENT_UNSOURCED : formatHydrationContentValue(field, value, locale),
    };
  });
}
